import type { DetailTab, DsBadge } from '@/features/data-stream/types';
/**
 * Debug Mode store — a scoped clone of the Data Stream store (live-only). Holds
 * raw MQTT messages for the topics the active tab is currently displaying
 * (filtered by useDebugScope before ingest), in per-topic ring buffers + a
 * topic-stat map, with pin-and-hold selection. Records are ephemeral; only the
 * pane/sidebar widths persist.
 */
import { classifyTopic } from '@/features/data-stream/utils/classifyTopic';
import type { RawMqttMessage } from '@/lib/mqtt/types';
import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';

/** Max retained messages per topic. */
export const RING_CAP = 200;

interface TopicStat {
  topic: string;
  count: number;
  lastSeen: number;
  lastQos: 0 | 1 | 2;
  badge: DsBadge;
}

interface PaneWidths {
  tree: number;
  messages: number;
}

interface DebugStore {
  topics: Map<string, TopicStat>;
  buffers: Map<string, RawMqttMessage[]>;
  selectedTopic: string | null;
  selectedMsgId: string | null;
  selectedMsg: RawMqttMessage | null;
  detailTab: DetailTab;
  search: string;
  paused: boolean;
  paneWidths: PaneWidths;
  sidebarWidth: number;

  ingestBatch: (msgs: RawMqttMessage[]) => void;
  selectTopic: (topic: string | null) => void;
  selectMsg: (id: string | null) => void;
  setTab: (t: DetailTab) => void;
  setSearch: (q: string) => void;
  togglePause: () => void;
  clear: () => void;
  setPaneWidth: (pane: keyof PaneWidths, px: number) => void;
  setSidebarWidth: (px: number) => void;
}

const DEFAULT_WIDTHS: PaneWidths = { tree: 200, messages: 240 };
export const SIDEBAR_DEFAULT = 640;

export const useDebugStore = create<DebugStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set) => ({
          topics: new Map(),
          buffers: new Map(),
          selectedTopic: null,
          selectedMsgId: null,
          selectedMsg: null,
          detailTab: 'json',
          search: '',
          paused: false,
          paneWidths: DEFAULT_WIDTHS,
          sidebarWidth: SIDEBAR_DEFAULT,

          ingestBatch: (msgs) =>
            set((s) => {
              if (s.paused || msgs.length === 0) return s;
              const topics = new Map(s.topics);
              const buffers = new Map(s.buffers);
              for (const msg of msgs) {
                const prev = topics.get(msg.topic);
                topics.set(msg.topic, {
                  topic: msg.topic,
                  count: (prev?.count ?? 0) + 1,
                  lastSeen: msg.receivedAt,
                  lastQos: msg.qos,
                  badge: prev?.badge ?? classifyTopic(msg.topic),
                });
                const cur = buffers.get(msg.topic) ?? [];
                const next = [msg, ...cur];
                if (next.length > RING_CAP) next.length = RING_CAP;
                buffers.set(msg.topic, next);
              }
              return { topics, buffers };
            }),
          selectTopic: (topic) =>
            set({ selectedTopic: topic, selectedMsgId: null, selectedMsg: null }),
          selectMsg: (id) =>
            set((s) => {
              if (id == null) return { selectedMsgId: null, selectedMsg: null };
              const buf = s.selectedTopic ? s.buffers.get(s.selectedTopic) : undefined;
              return { selectedMsgId: id, selectedMsg: buf?.find((m) => m.id === id) ?? null };
            }),
          setTab: (t) => set({ detailTab: t }),
          setSearch: (q) => set({ search: q }),
          togglePause: () => set((s) => ({ paused: !s.paused })),
          clear: () =>
            set({
              topics: new Map(),
              buffers: new Map(),
              selectedTopic: null,
              selectedMsgId: null,
              selectedMsg: null,
            }),
          setPaneWidth: (pane, px) => set((s) => ({ paneWidths: { ...s.paneWidths, [pane]: px } })),
          setSidebarWidth: (px) => set({ sidebarWidth: px }),
        }),
        {
          name: 'flysafe-v2-debug',
          partialize: (s) => ({ paneWidths: s.paneWidths, sidebarWidth: s.sidebarWidth }),
        },
      ),
    ),
    { name: 'debug' },
  ),
);
